/** Generic renderer for compiled procedural assets: one mesh per compiled
 * instance, PBR materials from the asset palette, sockets as empty Object3Ds.
 * Used by the asset lab viewport AND in-game (vehicle adapter below), so the
 * two always render an asset identically. Materials are DoubleSide because
 * mirror modifiers flip winding. */
import * as THREE from "three";
import {proceduralLathe} from './proceduralLathe';
import {
  compileAsset,
  type AssetMaterial,
  type CompiledAsset,
  type CompiledInstance,
  type ProceduralAssetV1,
} from "@hyperlight/shared";
import type { GroundVehicle } from "../vehicles/groundVehicle";
import type { VehicleView } from "../vehicles/proceduralSpeeder";

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

const AXIS_INDEX = { x: 0, y: 1, z: 2 } as const;

/** Apply taper/bend to a geometry in the instance's local frame. Convention
 * (shared with proceduralAssetCompile's doc comment): taper scales the two
 * non-axis components by lerp(1, factor, t), t 0→1 along the taper axis over
 * the geometry extent; bend rotates about the bend axis by angle*t around the
 * geometry center, t along +Y (or +X when bending about Y). */
function applyDeforms(geometry: THREE.BufferGeometry, deforms: NonNullable<CompiledInstance["deforms"]>): void {
  const positions = geometry.getAttribute("position") as THREE.BufferAttribute;
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const v = new THREE.Vector3();
  if (deforms.taper) {
    const axis = AXIS_INDEX[deforms.taper.axis];
    const minA = box.min.getComponent(axis), maxA = box.max.getComponent(axis);
    const span = Math.max(1e-6, maxA - minA);
    for (let i = 0; i < positions.count; i++) {
      v.fromBufferAttribute(positions, i);
      const t = (v.getComponent(axis) - minA) / span;
      const scale = lerp(1, deforms.taper.factor, t);
      for (let c = 0; c < 3; c++) if (c !== axis) v.setComponent(c, v.getComponent(c) * scale);
      positions.setXYZ(i, v.x, v.y, v.z);
    }
  }
  if (deforms.bend) {
    const bendAxis = AXIS_INDEX[deforms.bend.axis];
    const alongAxis = deforms.bend.axis === "y" ? 0 : 1;
    geometry.computeBoundingBox();
    const bb = geometry.boundingBox!;
    const center = bb.getCenter(new THREE.Vector3());
    const minA = bb.min.getComponent(alongAxis), maxA = bb.max.getComponent(alongAxis);
    const span = Math.max(1e-6, maxA - minA);
    const axisVec = new THREE.Vector3(); axisVec.setComponent(bendAxis, 1);
    const q = new THREE.Quaternion();
    for (let i = 0; i < positions.count; i++) {
      v.fromBufferAttribute(positions, i);
      const t = (v.getComponent(alongAxis) - minA) / span;
      q.setFromAxisAngle(axisVec, deforms.bend.angle * t);
      v.sub(center).applyQuaternion(q).add(center);
      positions.setXYZ(i, v.x, v.y, v.z);
    }
  }
  positions.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

function buildGeometry(instance: CompiledInstance): THREE.BufferGeometry {
  const p = instance.params;
  let geometry: THREE.BufferGeometry;
  switch (instance.kind) {
    case "box": geometry = new THREE.BoxGeometry(p[0], p[1], p[2], 2, 2, 2); break;
    case "cylinder": geometry = new THREE.CylinderGeometry(p[0], p[1], p[2], 24, 4); break;
    case "sphere": geometry = new THREE.SphereGeometry(p[0], 24, 16); break;
    case "cone": geometry = new THREE.ConeGeometry(p[0], p[1], 24, 4); break;
    case "capsule": geometry = new THREE.CapsuleGeometry(p[0], p[1], 8, 16); break;
    case "torus": geometry = new THREE.TorusGeometry(p[0], p[1], 12, 32); break;
    case "lathe": {
      geometry = proceduralLathe(instance);
      break;
    }
  }
  if (instance.deforms) applyDeforms(geometry, instance.deforms);
  return geometry;
}

function geometryKey(instance: CompiledInstance): string {
  return JSON.stringify([instance.kind, instance.params, instance.profile ?? null, instance.deforms ?? null, instance.faceting ?? 0]);
}

export class ProceduralAssetView {
  readonly group = new THREE.Group();
  readonly sockets = new Map<string, THREE.Object3D>();
  private readonly geometryCache = new Map<string, THREE.BufferGeometry>();
  private readonly materials: THREE.MeshStandardMaterial[] = [];

  constructor(compiled: CompiledAsset, materials: AssetMaterial[]) {
    for (const mat of materials) {
      const material = new THREE.MeshStandardMaterial({
        color: mat.color,
        roughness: mat.roughness,
        metalness: mat.metalness,
        side: THREE.DoubleSide,
      });
      if (mat.emissive !== undefined) {
        material.emissive = new THREE.Color(mat.emissive);
        material.emissiveIntensity = mat.emissiveIntensity ?? 1;
      }
      if (mat.opacity !== undefined && mat.opacity < 1) {
        material.transparent = true;
        material.opacity = mat.opacity;
      }
      this.materials.push(material);
    }
    if (!this.materials.length) {
      this.materials.push(new THREE.MeshStandardMaterial({ color: 0x8899aa, side: THREE.DoubleSide }));
    }
    const fallback = this.materials[0];
    for (const instance of compiled.instances) {
      const key = geometryKey(instance);
      let geometry = this.geometryCache.get(key);
      if (!geometry) { geometry = buildGeometry(instance); this.geometryCache.set(key, geometry); }
      const mesh = new THREE.Mesh(geometry, this.materials[instance.material] ?? fallback);
      mesh.matrixAutoUpdate = false;
      mesh.matrix.fromArray(instance.transform);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.nodeId = instance.nodeId;
      mesh.userData.generated = instance.generated;
      this.group.add(mesh);
    }
    const zAxis = new THREE.Vector3(), yAxis = new THREE.Vector3(), xAxis = new THREE.Vector3();
    const basis = new THREE.Matrix4();
    for (const socket of compiled.sockets) {
      const obj = new THREE.Object3D();
      obj.name = socket.name;
      obj.position.fromArray(socket.position);
      // socket local -Z = forward, +Y = up
      zAxis.fromArray(socket.forward).multiplyScalar(-1).normalize();
      yAxis.fromArray(socket.up).normalize();
      xAxis.crossVectors(yAxis, zAxis).normalize();
      yAxis.crossVectors(zAxis, xAxis).normalize();
      basis.makeBasis(xAxis, yAxis, zAxis);
      obj.quaternion.setFromRotationMatrix(basis);
      obj.userData.nodeId = socket.nodeId;
      obj.userData.generated = socket.generated;
      this.group.add(obj);
      this.sockets.set(socket.name, obj);
    }
  }

  dispose(): void {
    this.group.removeFromParent();
    this.group.clear();
    for (const geometry of this.geometryCache.values()) geometry.dispose();
    this.geometryCache.clear();
    for (const material of this.materials) material.dispose();
    this.sockets.clear();
  }
}

/** Drop-in VehicleView so lab-authored vehicles render in-game. The chassis is
 * static in v1 (no bank/thruster animation); boost FX read exhaust sockets. */
export class ProceduralAssetVehicleView implements VehicleView {
  readonly group = new THREE.Group();
  private readonly view: ProceduralAssetView;
  private readonly exhaust: THREE.Object3D[] = [];

  constructor(asset: ProceduralAssetV1) {
    this.view = new ProceduralAssetView(compileAsset(asset), asset.materials);
    this.group.add(this.view.group);
    for (const [name, socket] of this.view.sockets) {
      if (name.startsWith("exhaust")) this.exhaust.push(socket);
    }
  }

  update(_vehicle: GroundVehicle, _dt: number): void { /* static chassis in v1 */ }

  rearExhaustPoints(out: THREE.Vector3[]): number {
    let count = 0;
    for (const socket of this.exhaust) {
      if (count >= out.length) break;
      socket.getWorldPosition(out[count++]);
    }
    return count;
  }

  dispose(): void {
    this.view.dispose();
    this.group.removeFromParent();
  }
}
